import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jobsStats, listJobs, updateJob, type JobStats, type PrintJob } from "../api";
import { Icon } from "../components/Icon";
import { Empty } from "../components/ui";
import { useToast } from "../lib/toast";
import { PartThumb } from "../scene/PartThumb";

import { DATE_LOCALE, useLang, useT } from "../i18n";
const STATE: Record<string, { kind: string; ico: string; label: string }> = {
  queued: { kind: "", ico: "clock", label: "En file" },
  uploaded: { kind: "accent", ico: "wifi", label: "Envoyé" },
  printing: { kind: "warn", ico: "play", label: "Impression" },
  paused: { kind: "accent", ico: "pause", label: "Pause" },
  complete: { kind: "ok", ico: "check", label: "Terminé" },
  error: { kind: "bad", ico: "alert", label: "Erreur" },
};
const LIVE = ["queued", "uploaded", "printing", "paused"];
const NEXT: Record<string, string> = { queued: "printing", uploaded: "printing", printing: "complete" };

function fmtTime(s?: number | null): string {
  if (!s) return "--";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m.toString().padStart(2, "0")}m` : `${m}m`;
}

function OverviewCard({ v, l, ico, color }: { v: string | number; l: string; ico: string; color?: string }) {
  return (
    <div className="card stat" style={{ padding: 16 }}>
      <div className="ico" style={color ? { color, background: `color-mix(in oklab, ${color} 14%, transparent)`, borderColor: `color-mix(in oklab, ${color} 38%, var(--line-2))` } : undefined}><Icon name={ico} /></div>
      <div className="v" style={{ fontSize: 26, color }}>{v}</div>
      <div className="l">{l}</div>
    </div>
  );
}

export function JobsPage() {
  const tr = useT();
  const { lang } = useLang();
  const [jobs, setJobs] = useState<PrintJob[]>([]);
  const [stats, setStats] = useState<JobStats | null>(null);
  const toast = useToast((s) => s.show);
  const nav = useNavigate();

  const load = () => {
    listJobs().then(setJobs).catch(() => {});
    jobsStats().then(setStats).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  async function act(fn: () => Promise<unknown>, ok: string) {
    try { await fn(); toast(ok); load(); } catch (e) { toast(String((e as Error).message), "bad"); }
  }

  const RUNNING = ["uploaded", "printing", "paused"];
  const queue = jobs
    .filter((j) => j.state === "queued")
    .sort((a, b) => (a.scheduled_at ?? "9").localeCompare(b.scheduled_at ?? "9"));
  const live = jobs.filter((j) => RUNNING.includes(j.state));
  const history = jobs.filter((j) => !LIVE.includes(j.state));
  const done = jobs.filter((j) => j.state === "complete").length;
  const failed = jobs.filter((j) => j.state === "error").length;
  const successRate = done + failed ? Math.round((done / (done + failed)) * 100) : 100;
  const totalTime = history.reduce((a, j) => a + (j.print_time_s || 0), 0);
  const nowIso = new Date().toISOString();

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>FOUNDRY · Monitoring</div>
          <h1>{tr("Production")}</h1>
          <div className="sub">{tr("Suivi live et historique des impressions routées vers la ferme (Moonraker / OctoPrint / PrusaLink).")}</div>
        </div>
        <button className="btn ghost" onClick={load}><Icon name="route" size={15} /> {tr("Rafraîchir")}</button>
      </div>

      {/* Overview -- live counts + print-log aggregates (filament, cost, time) */}
      <div className="grid stats" style={{ marginBottom: 24 }}>
        <OverviewCard v={live.filter((j) => j.state === "printing").length} l="En impression" ico="play" color="var(--warn)" />
        <OverviewCard v={queue.length} l="En file d'attente" ico="clock" />
        <OverviewCard v={stats?.success_rate != null ? `${Math.round(stats.success_rate * 100)}%` : (done + failed ? `${successRate}%` : "--")} l="Taux de réussite" ico="check" color="var(--ok)" />
        <OverviewCard v={fmtTime(stats?.total_time_s ?? totalTime)} l="Temps cumulé" ico="layers" />
        <OverviewCard v={`${((stats?.total_filament_g ?? 0) / 1000).toFixed(2)} kg`} l="Filament consommé" ico="box" />
        <OverviewCard v={`${(stats?.total_cost ?? 0).toFixed(0)} €`} l="Coût cumulé" ico="card" />
      </div>

      {/* Queue -- queued jobs (batch + scheduled), ordered by schedule */}
      {queue.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <div className="row between" style={{ marginBottom: 14 }}>
            <div className="row" style={{ gap: 10 }}><h2>File d'attente</h2><span className="badge"><span className="num">{queue.length}</span></span></div>
            <span className="eyebrow">{tr("batch · planifié")}</span>
          </div>
          <div className="card" style={{ padding: 0 }}>
            <table className="table">
              <thead><tr><th>Fichier</th><th>Imprimante</th><th>{tr("Planifié")}</th><th>Filament</th><th style={{ textAlign: "right" }}>Job</th></tr></thead>
              <tbody>
                {queue.map((j) => {
                  const due = j.scheduled_at ? j.scheduled_at <= nowIso : true;
                  return (
                    <tr key={j.id} style={{ cursor: "pointer" }} onClick={() => nav(`/production/${j.id}`)}>
                      <td>
                        <div className="row" style={{ gap: 10 }}>
                          <PartThumb material={j.material} size={30} />
                          <span style={{ fontWeight: 600 }}>{j.filename}</span>
                          {j.copy && <span className="badge">{j.copy}</span>}
                        </div>
                      </td>
                      <td className="muted" style={{ fontSize: 12 }}>{j.printer_name}</td>
                      <td>
                        {j.scheduled_at
                          ? <span className={`badge ${due ? "ok" : "accent"}`}><Icon name="clock" size={11} />{due ? tr("À lancer") : new Date(j.scheduled_at).toLocaleString(DATE_LOCALE[lang], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                          : <span className="badge"><span className="dot" />Prêt</span>}
                      </td>
                      <td className="num muted" style={{ fontSize: 12 }}>{j.filament_used_g != null ? `${Math.round(j.filament_used_g)} g` : "--"}</td>
                      <td className="id" style={{ textAlign: "right" }}>{j.id.slice(0, 12)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Live */}
      <div className="row between" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 10 }}><h2>En direct</h2><span className="badge"><span className="num">{live.length}</span></span></div>
        <span className="eyebrow">live</span>
      </div>
      {live.length === 0 ? (
        <Empty icon="route">{tr("Aucune impression en cours. Routez un fichier prêt à imprimer depuis un projet.")}</Empty>
      ) : (
        <div className="grid cards" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", marginBottom: 28 }}>
          {live.map((j) => {
            const s = STATE[j.state] ?? STATE.queued;
            return (
              <div key={j.id} className="card">
                <div className="row" style={{ gap: 12, alignItems: "flex-start" }}>
                  <PartThumb material={j.material} size={56} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="card-row" style={{ marginBottom: 4 }}>
                      <span className="card-title" style={{ fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{j.filename}</span>
                      <span className={`badge ${s.kind}`}><Icon name={s.ico} size={11} />{tr(s.label)}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>{j.printer_name}{j.material ? ` · ${j.material}` : ""}</div>
                    {j.state === "printing" && (
                      <>
                        <div className="progress" style={{ marginTop: 9, height: 6 }}><span style={{ width: `${Math.round((j.progress || 0) * 100)}%`, background: "var(--warn)" }} /></div>
                        <div className="row between" style={{ marginTop: 5 }}>
                          <span className="num muted" style={{ fontSize: 11 }}>{Math.round((j.progress || 0) * 100)}%</span>
                          <span className="num muted" style={{ fontSize: 11 }}>{j.eta && j.eta !== "--" ? `ETA ${j.eta}` : ""}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {j.note && <div className="muted" style={{ fontSize: 12, marginTop: 10, padding: "8px 10px", background: "var(--surface-3)", borderRadius: 8 }}>📝 {j.note}</div>}
                <div className="row" style={{ gap: 6, marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--line)", justifyContent: "flex-end" }}>
                  <button className="btn sm ghost" title={tr("Détails du job")} onClick={() => nav(`/production/${j.id}`)}><Icon name="external" size={13} /> {tr("Détails")}</button>
                  <button className="btn sm ghost" title="Ajouter une note" onClick={() => { const n = prompt("Note (dev / amélioration / observation)", j.note || ""); if (n != null) act(() => updateJob(j.id, { note: n }), "Note enregistrée"); }}><Icon name="edit" size={13} /> Note</button>
                  {NEXT[j.state] && <button className="btn sm" onClick={() => act(() => updateJob(j.id, { state: NEXT[j.state] }), `Statut → ${tr(STATE[NEXT[j.state]].label)}`)}><Icon name="play" size={13} /> {tr(STATE[NEXT[j.state]].label)}</button>}
                  <button className="btn sm danger icon" title="Marquer en erreur" onClick={() => act(() => updateJob(j.id, { state: "error" }), "Marqué en erreur")}><Icon name="alert" size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* History */}
      <div className="row between" style={{ marginBottom: 14 }}>
        <div className="row" style={{ gap: 10 }}><h2>Historique</h2><span className="badge"><span className="num">{history.length}</span></span></div>
        <span className="eyebrow">{tr("timing · notes · améliorations")}</span>
      </div>
      {history.length === 0 ? (
        <Empty icon="clock">{tr("Pas encore d'impressions terminées.")}</Empty>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>Fichier</th><th>{tr("Imprimante · matière")}</th><th>Statut</th><th>Durée</th><th>Note</th><th style={{ textAlign: "right" }}>Job</th></tr></thead>
            <tbody>
              {history.map((j) => {
                const s = STATE[j.state] ?? STATE.complete;
                return (
                  <tr key={j.id}>
                    <td><div className="row" style={{ gap: 10 }}><PartThumb material={j.material} passed={j.state === "complete"} size={34} /><span style={{ fontWeight: 600 }}>{j.filename}</span></div></td>
                    <td className="muted" style={{ fontSize: 12 }}>{j.printer_name}{j.material ? ` · ${j.material}` : ""}</td>
                    <td><span className={`badge ${s.kind}`}><Icon name={s.ico} size={11} />{tr(s.label)}</span></td>
                    <td className="num" style={{ fontSize: 12 }}>{fmtTime(j.print_time_s)}</td>
                    <td className="muted" style={{ fontSize: 12, maxWidth: 220 }}>
                      <button className="btn sm ghost" onClick={() => { const n = prompt("Note", j.note || ""); if (n != null) act(() => updateJob(j.id, { note: n }), "Note enregistrée"); }}>{j.note ? `📝 ${j.note.slice(0, 28)}${j.note.length > 28 ? "…" : ""}` : "+ note"}</button>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <a className="id" style={{ cursor: "pointer", color: "var(--accent-3)" }} title={tr("Détails du job")} onClick={() => nav(`/production/${j.id}`)}>{j.id.slice(0, 12)}</a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
